import React from 'react';

/**
 * Small self-contained "upload in progress" popup shown when the user tries to
 * start a mobile / Apple Photos upload while another upload is still running.
 * Inline-styled (no CSS module) so it looks identical wherever it is used
 * (main-app upload dropdown + new-signup gift-content flow).
 */
const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0, 0, 0, 0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 100000,
  padding: 16,
};

// Per-theme colours. `dark` (default) suits the signup / gift-content dark
// pages; `light` suits the main-app (white) surfaces.
const THEMES = {
  dark: {
    card: '#1c2030',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    title: '#fff',
    text: '#c7ccd8',
  },
  light: {
    card: '#fff',
    border: '1px solid rgba(0, 0, 0, 0.1)',
    title: '#02071A',
    text: '#555',
  },
};

const baseCardStyle = {
  borderRadius: 14,
  padding: '26px 24px',
  width: '100%',
  maxWidth: 340,
  textAlign: 'center',
  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.45)',
  fontFamily: "'Montserrat', sans-serif",
};

const iconWrapStyle = { marginBottom: 12, lineHeight: 0 };

const baseTitleStyle = {
  fontSize: '1.15rem',
  fontWeight: 600,
  margin: '0 0 8px 0',
};

const baseTextStyle = {
  fontSize: '0.9rem',
  lineHeight: 1.5,
  margin: '0 0 20px 0',
};

const buttonStyle = {
  background: '#5aa5ef',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '10px 28px',
  fontSize: '0.95rem',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: "'Montserrat', sans-serif",
};

const UploadInProgressModal = ({ open, onClose, message, theme = 'dark' }) => {
  if (!open) return null;
  const t = THEMES[theme] || THEMES.dark;
  return (
    <div style={overlayStyle} role="dialog" aria-modal="true">
      <div style={{ ...baseCardStyle, background: t.card, border: t.border }}>
        <div style={iconWrapStyle} aria-hidden="true">
          {/* Vector clock icon — inherits the theme colour (near-black on the
              light main-app theme, light on the dark signup theme). */}
          <svg
            width="44"
            height="44"
            viewBox="0 0 24 24"
            fill="none"
            stroke={t.title}
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3.5 2" />
          </svg>
        </div>
        <h3 style={{ ...baseTitleStyle, color: t.title }}>Upload in progress</h3>
        <p style={{ ...baseTextStyle, color: t.text }}>
          {message || 'An upload is already in progress. Please wait until it is completed before starting a new upload.'}
        </p>
        <button type="button" style={buttonStyle} onClick={onClose}>OK</button>
      </div>
    </div>
  );
};

export default UploadInProgressModal;
